import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,

  Check,
  SpinnerIcon,
  FloppyDiskIcon,
  Play,
  X,

  FileXls,
  FileCsv,
} from '@phosphor-icons/react';
import ExcelJS from 'exceljs';
import { templateApi } from '../services/api';
import { useToastActions } from '../components/ui/Toast';
import { useWorkflowStore } from '../stores/workflowStore';
import { useWorkflowListStore } from '../stores/workflowListStore';
import { WorkflowExecutionResult } from '../components/workflow/WorkflowExecutionResult';
import type { Template, FieldMapping, MatchSuggestion, WorkflowExecutionResult as WorkflowExecutionResultType } from '../types/template';
import type { NodeDSL, EdgeDSL } from '../types';
import { WorkflowReadOnlyCanvas } from '../components/workflow/WorkflowReadOnlyCanvas';
import { ShareDialog } from '../components/share/ShareDialog';

type WizardStep = 'upload' | 'mapping' | 'result';

// 模板返回的节点/边可能是大写 ID 或缺少 position，在此统一为带布局的节点与边
type ApiNode = {
  id?: string;
  ID?: string;
  type: string;
  label?: string;
  position?: { x: number; y: number };
  config?: Record<string, unknown>;
};
type ApiEdge = { id?: string; source: string; target: string };

const LOAD_NODE_TYPES = new Set(['load_excel', 'load_csv', 'load_table']);
const MULTI_INPUT_NODE_TYPES = new Set(['diff', 'join', 'append']);
const LAYOUT_ORIGIN = { x: 200, y: 80 };
const LAYOUT_DY = 140;

// 输入节点信息
interface InputNode {
  id: string;
  type: 'load_excel' | 'load_csv' | 'load_table';
  label: string;
  /** For load_table: source format from node params */
  tableFormat?: 'excel' | 'csv';
  /** 虚拟节点的目标多输入节点 ID（如 diff 节点） */
  virtualTarget?: string;
}

function templateNodeParams(node: { config?: Record<string, unknown> }): Record<string, unknown> {
  const c = node.config ?? {};
  const p = c.params;
  if (p && typeof p === 'object' && !Array.isArray(p)) {
    return p as Record<string, unknown>;
  }
  return c;
}

function normalizeWorkflowFromTemplate(
  nodes: ApiNode[],
  edges: ApiEdge[]
): { nodes: ApiNode[]; edges: ApiEdge[] } {
  if (nodes.length === 0) return { nodes: [], edges: [] };

  const normalizedNodes = nodes.map((n) => ({
    id: n.id ?? n.ID ?? '',
    type: n.type,
    label: n.label ?? n.type,
    position: n.position,
    config: n.config ?? {},
  }));

  const hasLoadNode = normalizedNodes.some((n) => LOAD_NODE_TYPES.has(n.type));
  const targetIds = new Set(edges.map((e) => e.target));
  const entryNodeIds = new Set(normalizedNodes.map((n) => n.id).filter((id) => !targetIds.has(id)));

  let finalNodes = normalizedNodes;
  let finalEdges = [...edges];

  if (!hasLoadNode && entryNodeIds.size > 0) {
    const loadId = 'load';
    finalNodes = [
      { id: loadId, type: 'load_table', label: 'Load Table', position: undefined, config: { tableFormat: 'excel' } },
      ...normalizedNodes,
    ];
    entryNodeIds.forEach((targetId) => {
      finalEdges.push({ source: loadId, target: targetId });
    });
  }

  const nodeIds = new Set(finalNodes.map((n) => n.id));
  const order: string[] = [];
  const inDegree: Record<string, number> = {};
  nodeIds.forEach((id) => (inDegree[id] = 0));
  finalEdges.forEach((e) => (inDegree[e.target] = (inDegree[e.target] ?? 0) + 1));
  const queue = finalNodes.filter((n) => inDegree[n.id] === 0).map((n) => n.id);
  while (queue.length > 0) {
    const id = queue.shift()!;
    order.push(id);
    finalEdges.forEach((e) => {
      if (e.source === id) {
        inDegree[e.target]--;
        if (inDegree[e.target] === 0) queue.push(e.target);
      }
    });
  }
  const orderedIds = [...order, ...finalNodes.map((n) => n.id).filter((id) => !order.includes(id))];
  const positionById: Record<string, { x: number; y: number }> = {};
  orderedIds.forEach((id, i) => {
    positionById[id] = { x: LAYOUT_ORIGIN.x, y: LAYOUT_ORIGIN.y + i * LAYOUT_DY };
  });

  const nodesWithLayout = finalNodes.map((n) => ({
    ...n,
    position: n.position?.x != null && n.position?.y != null ? n.position : positionById[n.id] ?? { x: 0, y: 0 },
  }));

  return { nodes: nodesWithLayout, edges: finalEdges };
}

/** 将模板/后端返回的节点 config 转为前端各配置组件期望的 params 结构 */
function normalizeNodeParams(
  nodeType: string,
  config: Record<string, unknown> | undefined
): Record<string, unknown> {
  const c = config ?? {};
  switch (nodeType) {
    case 'group_by':
      return { groupBy: (c.groupBy as string[]) ?? (c.columns as string[]) ?? [] };
    case 'compute':
      if (c.expressions != null && Array.isArray(c.expressions) && c.expressions.length > 0) {
        return { expressions: c.expressions };
      }
      if (typeof c.expression === 'string' && typeof c.newColumn === 'string') {
        return { expressions: [{ column: c.newColumn, formula: c.expression }] };
      }
      return { expressions: c.expressions ?? [{ column: '', formula: '' }] };
    case 'dedup':
      return { columns: c.columns ?? [], keepFirst: c.keepFirst !== false };
    case 'aggregate':
      return { groupBy: c.groupBy ?? [], aggregations: c.aggregations ?? [{ column: '', function: 'sum' }] };
    case 'filter':
      return { conditions: c.conditions ?? [{ column: '', operator: '==', value: '', logic: 'AND' }] };
    case 'sort':
      return { fields: c.fields ?? [{ column: '', order: 'asc' }] };
    case 'transform':
      return { rules: c.rules ?? [], ...c };
    case 'load_excel':
    case 'load_csv':
    case 'load_table':
      return { ...c };
    case 'export':
      return { ...c };
    default:
      return { ...c };
  }
}

function i18nInputLabel(index: number): string {
  return `输入 #${index}`;
}

/** 从模板工作流定义中提取输入节点。
 *  对于多输入节点（diff/join/append），如果缺少 load 节点输入，
 *  会自动补充虚拟 load 节点以确保用户可以上传所有需要的文件。 */
function extractInputNodes(template: Template | null): InputNode[] {
  if (!template?.workflowDefinition?.nodes) return [];

  const nodes = template.workflowDefinition.nodes;
  const edges = template.workflowDefinition.edges ?? [];
  const inputNodes: InputNode[] = [];
  const loadNodeIds = new Set<string>();

  for (const node of nodes) {
    if (LOAD_NODE_TYPES.has(node.type)) {
      const p = templateNodeParams(node);
      const tf =
        node.type === 'load_table' ? (p.tableFormat === 'csv' ? ('csv' as const) : ('excel' as const)) : undefined;
      inputNodes.push({
        id: node.id,
        type: node.type as 'load_excel' | 'load_csv' | 'load_table',
        label: node.label || node.type,
        tableFormat: tf,
      });
      loadNodeIds.add(node.id);
    }
  }

  // 检查多输入节点（diff/join/append）是否缺少 load 节点输入
  // 例如 diff 需要 2 个输入，如果模板只定义了 1 个 load 节点，需要补充虚拟上传区域
  for (const node of nodes) {
    if (!MULTI_INPUT_NODE_TYPES.has(node.type)) continue;

    const sourceIds = edges.filter((e) => e.target === node.id).map((e) => e.source);
    const loadSourceCount = sourceIds.filter((id) => loadNodeIds.has(id)).length;
    const minInputs = 2;
    const neededExtra = Math.max(0, minInputs - Math.max(loadSourceCount, inputNodes.length));

    for (let i = 0; i < neededExtra; i++) {
      const virtualId = `__vl_${node.id}_${i}`;
      inputNodes.push({
        id: virtualId,
        type: 'load_table',
        label: `${node.label || node.type} - ${i18nInputLabel(loadSourceCount + i + 1)}`,
        tableFormat: 'excel',
        virtualTarget: node.id,
      });
    }
  }

  // 如果没有找到输入节点，默认添加一个
  if (inputNodes.length === 0) {
    inputNodes.push({
      id: 'load',
      type: 'load_table',
      label: 'Load Table',
      tableFormat: 'excel',
    });
  }

  return inputNodes;
}

export function TemplateApplyPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const toast = useToastActions();
  const { t, i18n } = useTranslation();
  const { loadWorkflow, clearWorkflow } = useWorkflowStore();
  const { addWorkflow } = useWorkflowListStore();

  // 状态
  const [template, setTemplate] = useState<Template | null>(null);
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  
  // 多文件状态：key 是节点ID
  const [files, setFiles] = useState<Record<string, File>>({});
  const [, setFilesInfo] = useState<Record<string, { name: string; rowCount: number; columns: string[]; data: Record<string, unknown>[] }>>({});
  
  // 使用第一个输入文件进行分析
  const [analyzingNodeId, setAnalyzingNodeId] = useState<string | null>(null);
  const [fileAnalyses, setFileAnalyses] = useState<Record<string, {
    headers: string[];
    previewData: string[][];
    suggestions: MatchSuggestion[];
  }>>({});
  
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [savedMappings, setSavedMappings] = useState<FieldMapping[]>([]);
  const [selectedMappingId, setSelectedMappingId] = useState<string>('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<WorkflowExecutionResultType | null>(null);
  const [executionId, setExecutionId] = useState<string>('');
  const [mappingName, setMappingName] = useState('');
  const [showSaveMappingDialog, setShowSaveMappingDialog] = useState(false);
  const [showSaveWorkflowDialog, setShowSaveWorkflowDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [workflowNameDraft, setWorkflowNameDraft] = useState('');
  const [workflowDefinition, setWorkflowDefinition] = useState<Template['workflowDefinition'] | null>(null);

  const normalizedWorkflowForPreview = useMemo(() => {
    const def = workflowDefinition ?? template?.workflowDefinition;
    if (!def) return null;
    return normalizeWorkflowFromTemplate(def.nodes as ApiNode[], def.edges as ApiEdge[]);
  }, [workflowDefinition, template]);

  const previewNodes = useMemo(() => {
    if (!normalizedWorkflowForPreview) return [];
    return normalizedWorkflowForPreview.nodes.map((n) => ({
      id: n.id ?? '',
      type: n.type,
      position: n.position,
      // WorkflowReadOnlyCanvas 会读取 label（虽不在 NodeDSL 上），这里保留供展示
      label: n.label,
      config: n.config ?? {},
    })) as unknown as NodeDSL[];
  }, [normalizedWorkflowForPreview]);

  const previewEdges = useMemo(() => {
    if (!normalizedWorkflowForPreview) return [];
    return normalizedWorkflowForPreview.edges.map((e) => ({ from: e.source, to: e.target })) as EdgeDSL[];
  }, [normalizedWorkflowForPreview]);

  // 计算输入节点
  const inputNodes = useMemo(() => extractInputNodes(template), [template]);
  const currentLocale = useMemo(() => {
    const lang = (i18n.resolvedLanguage || i18n.language || 'zh').toLowerCase();
    if (lang.startsWith('en')) return 'en';
    if (lang.startsWith('ja')) return 'ja';
    return 'zh';
  }, [i18n.language, i18n.resolvedLanguage]);

  const getLocalizedFieldName = (field: Template['fields'][number]) => {
    const localized = field.nameI18n?.[currentLocale]
      ?? field.nameI18n?.zh
      ?? field.nameI18n?.en
      ?? field.nameI18n?.ja;
    if (localized) return localized;

    const synonyms = field.synonyms ?? [];

    const findEnglishCandidate = () =>
      synonyms.find((s) => /^[\x00-\x7F\s._-]+$/.test(s) && /[A-Za-z]/.test(s));
    const findJapaneseCandidate = () =>
      synonyms.find((s) => /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff]/.test(s));

    if (currentLocale === 'en') {
      const enSynonym = findEnglishCandidate();
      if (enSynonym) return enSynonym;
      // Fallback for APIs that only return canonical id and Chinese name.
      return field.id
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    if (currentLocale === 'ja') {
      const jaSynonym = findJapaneseCandidate();
      if (jaSynonym) return jaSynonym;
    }

    return field.name;
  };
  
  // 获取第一个输入节点用于字段分析
  const primaryInputNode = inputNodes[0];
  const primaryFileAnalysis = primaryInputNode?.id ? (fileAnalyses[primaryInputNode.id] ?? null) : null;

  const loadPlaceholderFieldIds = useMemo(() => {
    const def = template?.workflowDefinition;
    if (!def?.nodes) return new Set<string>();
    const ids = new Set<string>();
    for (const n of def.nodes as ApiNode[]) {
      if (!LOAD_NODE_TYPES.has(n.type)) continue;
      const src = (n.config as { source?: unknown } | undefined)?.source;
      if (typeof src !== 'string') continue;
      const m = src.match(/^\s*\{\{\s*([^}]+?)\s*\}\}\s*$/);
      if (m?.[1]) ids.add(m[1]);
    }
    return ids;
  }, [template]);

  const fieldsForMapping = useMemo(() => {
    if (!template) return [];
    return template.fields.filter((f) => !loadPlaceholderFieldIds.has(f.id));
  }, [template, loadPlaceholderFieldIds]);

  // 加载模板和保存的映射
  useEffect(() => {
    if (templateId) {
      loadTemplate();
      loadSavedMappings();
    }
  }, [templateId]);

  const loadTemplate = async () => {
    try {
      const response = await templateApi.getTemplate(templateId!);
      if (response.code === 0) {
        setTemplate(response.data);
      }
    } catch (error) {
      toast.error(t('templates.apply.error_loading'));
      navigate('/templates');
    }
  };

  const loadSavedMappings = async () => {
    try {
      const response = await templateApi.listMappings(templateId);
      if (response.code === 0) {
        setSavedMappings(response.data.mappings);
      }
    } catch (error) {
      console.error('Failed to load mappings', error);
    }
  };

  // 检查是否所有文件都已上传
  const areAllFilesUploaded = useMemo(() => {
    return inputNodes.length > 0 && inputNodes.every(node => files[node.id]);
  }, [inputNodes, files]);

  // 检查是否可以进行下一步（mapping）：虚拟节点无需字段分析
  const canProceedToMapping = useMemo(() => {
    if (!areAllFilesUploaded) return false;
    return inputNodes
      .filter((n) => !n.virtualTarget)
      .every((n) => Boolean(fileAnalyses[n.id]));
  }, [areAllFilesUploaded, fileAnalyses, inputNodes]);

  // 文件上传处理（针对特定节点）
  const handleFileUpload = async (nodeId: string, uploadedFile: File) => {
    if (!template || !primaryInputNode) return;

    const nodeInfo = inputNodes.find((n) => n.id === nodeId);
    setFiles(prev => ({ ...prev, [nodeId]: uploadedFile }));

    // 虚拟节点（多输入节点的补充文件）不需要字段分析，直接记录即可
    if (nodeInfo?.virtualTarget) {
      const fileInfo = await parseFileBasicInfo(uploadedFile);
      setFilesInfo(prev => ({ ...prev, [nodeId]: fileInfo }));
      return;
    }

    setAnalyzingNodeId(nodeId);

    try {
      const analysis = await templateApi.analyzeFile(template.id, uploadedFile);
      setFileAnalyses((prev) => ({ ...prev, [nodeId]: analysis }));

      setMappings((prev) => {
        const next = { ...prev };
        analysis.suggestions.forEach((s) => {
          if (s.confidence > 0.5 && !next[s.templateFieldId]) {
            next[s.templateFieldId] = s.excelColumn;
          }
        });
        return next;
      });

      const fileInfo = await parseFileBasicInfo(uploadedFile);
      setFilesInfo(prev => ({ ...prev, [nodeId]: fileInfo }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('templates.apply.error_analysis'));
      setFiles(prev => {
        const newFiles = { ...prev };
        delete newFiles[nodeId];
        return newFiles;
      });
    } finally {
      setAnalyzingNodeId(null);
    }
  };

  // 解析文件基本信息（行数、列等）
  const parseFileBasicInfo = async (file: File): Promise<{ name: string; rowCount: number; columns: string[]; data: Record<string, unknown>[] }> => {
    // 这里简化处理，实际可能需要读取文件内容
    return {
      name: file.name,
      rowCount: 0, // 实际使用时需要解析
      columns: [],
      data: [],
    };
  };

  // 进入 mapping 步骤
  const handleProceedToMapping = () => {
    if (!canProceedToMapping) {
      toast.error(t('templates.apply.please_upload_all_files'));
      return;
    }
    setCurrentStep('mapping');
  };

  // 应用保存的映射
  const handleApplySavedMapping = (mappingId: string) => {
    const mapping = savedMappings.find((m) => m.id === mappingId);
    if (mapping) {
      setMappings(mapping.mappings);
      setSelectedMappingId(mappingId);
    }
  };

  // 保存映射
  const handleSaveMapping = async () => {
    if (!template || !mappingName.trim()) return;

    try {
      const response = await templateApi.saveMapping({
        templateId: template.id,
        name: mappingName.trim(),
        mappings,
        isDefault: false,
      });

      if (response.code === 0) {
        toast.success(t('templates.apply.mapping_saved'));
        setShowSaveMappingDialog(false);
        setMappingName('');
        loadSavedMappings();
      }
    } catch (error) {
      toast.error(t('templates.apply.error_saving_mapping'));
    }
  };

  // 验证映射
  const validateMappings = () => {
    if (!template) return false;

    const requiredFields = template.fields.filter((f) => f.required && !loadPlaceholderFieldIds.has(f.id));
    const missingFields = requiredFields.filter((f) => !mappings[f.id]);

    if (missingFields.length > 0) {
      toast.error(`${t('templates.apply.required_fields')}: ${missingFields.map((f) => getLocalizedFieldName(f)).join(', ')}`);
      return false;
    }

    return true;
  };

  // 执行模板
  const handleExecute = async () => {
    if (!template || !areAllFilesUploaded || !validateMappings()) return;

    setIsExecuting(true);
    try {
      // 构建文件数组
      const fileArray = inputNodes.map(node => ({
        nodeId: node.id,
        file: files[node.id],
      }));

      // 构建虚拟节点 → 目标节点映射（用于 diff/join/append 等多输入节点）
      const virtualTargets: Record<string, string> = {};
      for (const node of inputNodes) {
        if (node.virtualTarget) {
          virtualTargets[node.id] = node.virtualTarget;
        }
      }

      const result = await templateApi.applyTemplate(template.id, fileArray, mappings, virtualTargets);
      setExecutionResult(result);
      setExecutionId(result.executionId || '');
      setWorkflowDefinition(result.workflowDefinition);
      setCurrentStep('result');
      toast.success(t('templates.apply.execution_success', { count: result.rowCount }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('templates.apply.error_execution'));
    } finally {
      setIsExecuting(false);
    }
  };

  const getDefaultWorkflowName = () => {
    if (!template) return '';
    return `${template.name}_${files[primaryInputNode?.id]?.name || 'workflow'}`;
  };

  // 打开“保存为工作流”弹窗
  const handleOpenSaveAsWorkflow = () => {
    if (!template || !areAllFilesUploaded || !workflowDefinition) return;
    setWorkflowNameDraft(getDefaultWorkflowName());
    setShowSaveWorkflowDialog(true);
  };

  // 保存为工作流
  const handleSaveAsWorkflow = async () => {
    if (!template || !areAllFilesUploaded || !workflowDefinition) return;
    const workflowName = workflowNameDraft.trim();
    if (!workflowName) {
      toast.error(t('templates.apply.workflowNameRequired'));
      return;
    }

    try {
      const { nodes: defNodes, edges: defEdges } = normalizeWorkflowFromTemplate(
        workflowDefinition.nodes ?? [],
        workflowDefinition.edges ?? []
      );

      const now = new Date().toISOString();
      const workflowId = `wf_${Date.now()}`;

      const paramsFor = (n: ApiNode) =>
        normalizeNodeParams(n.type, n.config as Record<string, unknown> | undefined);

      const workflow = {
        id: workflowId,
        name: workflowName,
        description: t('templates.apply.created_from_template', { name: template.name }),
        dag: {
          nodes: Object.fromEntries(
            defNodes.map((n) => [
              n.id,
              {
                id: n.id,
                type: n.type,
                config: { type: n.type, params: paramsFor(n) },
                position: n.position ?? { x: 0, y: 0 },
                inputs: [],
              },
            ])
          ),
          edges: defEdges.map((e) => ({ from: e.source, to: e.target })),
        },
        createdAt: now,
        updatedAt: now,
      };

      const savedWorkflow = {
        id: workflowId,
        name: workflowName,
        description: workflow.description,
        status: 'draft' as const,
        nodes: defNodes.map((n) => ({
          id: n.id ?? '',
          type: n.type,
          position: n.position ?? { x: 0, y: 0 },
          data: {
            label: n.label ?? n.type,
            config: { type: n.type, params: paramsFor(n) },
          },
        })),
        edges: defEdges.map((e) => ({
          id: e.id ?? `e_${e.source}_${e.target}`,
          source: e.source,
          target: e.target,
        })),
        nodeCount: defNodes.length,
        executionCount: 0,
        uploadedFile: null,
        createdAt: now,
        updatedAt: now,
      };
      const created = await addWorkflow(savedWorkflow);

      clearWorkflow();
      loadWorkflow(workflow);

      toast.success(t('templates.apply.workflow_saved'));
      setShowSaveWorkflowDialog(false);
      navigate(`/workflows/store/edit/${created.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('templates.apply.error_save_workflow'));
    }
  };

  // 下载执行结果
  const handleDownloadResult = async () => {
    if (!executionResult) return;

    const toExcelCellValue = (value: unknown): string | number | boolean => {
      if (value == null) return '';
      if (typeof value === 'number' || typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        // Force potential formulas (e.g. =IMAGE(...)) to stay as plain text.
        if (/^[=+\-@]/.test(value)) return `'${value}`;
        return value;
      }

      try {
        const serialized = JSON.stringify(value);
        if (serialized && /^[=+\-@]/.test(serialized)) return `'${serialized}`;
        return serialized ?? String(value);
      } catch {
        const fallback = String(value);
        if (/^[=+\-@]/.test(fallback)) return `'${fallback}`;
        return fallback;
      }
    };

    const parseBase64Image = (value: unknown): { extension: 'png' | 'jpeg'; base64: string } | null => {
      if (typeof value !== 'string') return null;
      const matched = value.match(/^data:image\/(png|jpe?g);base64,([\s\S]+)$/i);
      if (!matched) return null;
      const ext = matched[1].toLowerCase();
      return {
        extension: ext === 'png' ? 'png' : 'jpeg',
        base64: matched[2],
      };
    };

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Result');

      worksheet.addRow(executionResult.columns);

      executionResult.rows.forEach((row) => {
        const rowValues = executionResult.columns.map((col) => toExcelCellValue(row[col]));
        worksheet.addRow(rowValues);
      });

      executionResult.rows.forEach((row, rowIndex) => {
        executionResult.columns.forEach((col, colIndex) => {
          const imagePayload = parseBase64Image(row[col]);
          if (!imagePayload) return;

          const excelRow = rowIndex + 2;
          const excelCol = colIndex + 1;
          const imageId = workbook.addImage({
            base64: imagePayload.base64,
            extension: imagePayload.extension,
          });

          worksheet.getCell(excelRow, excelCol).value = '';
          worksheet.getRow(excelRow).height = 90;
          worksheet.getColumn(excelCol).width = Math.max(worksheet.getColumn(excelCol).width ?? 10, 18);

          worksheet.addImage(imageId, {
            tl: { col: excelCol - 1, row: excelRow - 1 },
            ext: { width: 120, height: 120 },
            editAs: 'oneCell',
          });
        });
      });

      const excelBuffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template?.name || 'result'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('templates.apply.error_execution'));
    }
  };

  // 获取节点图标
  const getNodeIcon = (node: InputNode) => {
    const csvLike = node.type === 'load_csv' || (node.type === 'load_table' && node.tableFormat === 'csv');
    if (csvLike) {
      return <FileCsv className="w-6 h-6 text-emerald-500" />;
    }
    return <FileXls className="w-6 h-6 text-emerald-500" />;
  };

  // 步骤指示器
  const steps: { id: WizardStep; labelKey: string }[] = [
    { id: 'upload', labelKey: 'templates.apply.stepUpload' },
    { id: 'mapping', labelKey: 'templates.apply.stepMapping' },
    { id: 'result', labelKey: 'templates.apply.stepResult' },
  ];

  if (!template) {
    return (
      <div className="h-full flex items-center justify-center">
        <SpinnerIcon className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-50">
      {/* 头部 */}
      <div className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/templates')}
            className="p-2 text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">{t('templates.apply.title')}: {template.name}</h1>
            <p className="text-sm text-zinc-500">{template.description}</p>
          </div>
        </div>

        {/* 步骤指示器 */}
        <div className="flex items-center gap-2 mt-6">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  currentStep === step.id
                    ? 'bg-emerald-50 text-emerald-600'
                    : index < steps.findIndex((s) => s.id === currentStep)
                    ? 'bg-zinc-100 text-zinc-900'
                    : 'text-zinc-400'
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                    currentStep === step.id
                      ? 'bg-emerald-500 text-white'
                      : index < steps.findIndex((s) => s.id === currentStep)
                      ? 'bg-zinc-900 text-white'
                      : 'bg-zinc-200 text-zinc-500'
                  }`}
                >
                  {index < steps.findIndex((s) => s.id === currentStep) ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    index + 1
                  )}
                </span>
                {t(step.labelKey)}
              </div>
              {index < steps.length - 1 && (
                <ArrowRight className="w-4 h-4 text-zinc-300 mx-2" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 内容区域 */}
      <div className="flex-1 overflow-auto p-6">
        <AnimatePresence mode="wait">
          {/* 步骤 1: 上传文件 */}
          {currentStep === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-4xl mx-auto"
            >
              {/* 输入节点文件上传区域 */}
              <div className="bg-white rounded-2xl border border-zinc-200 p-6 mb-6">
                <h3 className="text-lg font-medium text-zinc-900 mb-2">
                  {t('templates.apply.uploadFilesTitle') || '上传输入文件'}
                </h3>
                <p className="text-sm text-zinc-500 mb-6">
                  {inputNodes.length > 1 
                    ? t('templates.apply.uploadMultipleDesc') || `此模板需要 ${inputNodes.length} 个输入文件，请为每个节点上传对应的文件`
                    : t('templates.apply.uploadDesc')}
                </p>

                <div className={`grid gap-4 ${inputNodes.length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 max-w-xl'}`}>
                  {inputNodes.map((node) => {
                    const file = files[node.id];
                    const isAnalyzing = analyzingNodeId === node.id;
                    const isPrimary = node.id === primaryInputNode?.id;

                    return (
                      <div
                        key={node.id}
                        className={`relative border-2 border-dashed rounded-2xl p-6 transition-all ${
                          file
                            ? 'border-emerald-400 bg-emerald-50/30'
                            : 'border-zinc-300 hover:border-emerald-400 hover:bg-emerald-50/30'
                        }`}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const droppedFile = e.dataTransfer.files[0];
                          if (droppedFile) {
                            handleFileUpload(node.id, droppedFile);
                          }
                        }}
                      >
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={(e) => {
                            const selectedFile = e.target.files?.[0];
                            if (selectedFile) {
                              handleFileUpload(node.id, selectedFile);
                            }
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />

                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            {isAnalyzing ? (
                              <SpinnerIcon className="w-6 h-6 text-emerald-500 animate-spin" />
                            ) : file ? (
                              <Check className="w-6 h-6 text-emerald-500" weight="bold" />
                            ) : (
                              getNodeIcon(node)
                            )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-zinc-900">
                                  {node.label}
                                </h4>
                                {isPrimary && (
                                  <span className="px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded-full">
                                    {t('templates.apply.primary') || '主文件'}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-zinc-500 mb-2">
                                {node.type === 'load_csv' || (node.type === 'load_table' && node.tableFormat === 'csv')
                                  ? 'CSV'
                                  : 'Excel'}{' '}
                                {t('templates.apply.file') || '文件'}
                              </p>

                              {file ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-zinc-700 truncate">{file.name}</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFiles(prev => {
                                        const newFiles = { ...prev };
                                        delete newFiles[node.id];
                                        return newFiles;
                                      });
                                      setFileAnalyses((prev) => {
                                        const next = { ...prev };
                                        delete next[node.id];
                                        return next;
                                      });
                                    }}
                                    className="p-1 text-zinc-400 hover:text-red-500 transition-colors"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <p className="text-sm text-zinc-400">
                                  {isAnalyzing 
                                    ? t('templates.apply.analyzing') || '分析中...'
                                    : t('templates.apply.clickOrDrag') || '点击或拖拽文件到此处'}
                                </p>
                              )}
                            </div>
                          </div>
                      </div>
                    );
                  })}
                </div>

                {/* 工作流预览（只读） */}
                {previewNodes.length > 0 && (
                  <div className="mt-6 rounded-2xl border border-zinc-200 overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4 bg-zinc-50 border-b border-zinc-200">
                      <h4 className="text-sm font-medium text-zinc-900">
                        {t('templates.apply.workflowPreview') || '流程预览'}
                      </h4>
                      <div className="text-xs text-zinc-500">
                        {t('templates.apply.workflowPreviewHint') || '只读预览，用于确认模板流程是否符合预期'}
                      </div>
                    </div>
                    <div className="h-[340px] bg-white">
                      <WorkflowReadOnlyCanvas
                        nodes={previewNodes}
                        edges={previewEdges}
                        className="h-full"
                      />
                    </div>
                  </div>
                )}

                {/* 下一步按钮 */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleProceedToMapping}
                    disabled={!canProceedToMapping}
                    className="flex items-center gap-2 px-6 py-2 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('templates.apply.nextStep') || '下一步'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* 模板字段预览 */}
              <div className="bg-white rounded-2xl border border-zinc-200 p-6">
                <h4 className="font-medium text-zinc-900 mb-4">{t('templates.apply.templateFields')}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {template.fields.map((field) => (
                    <div
                      key={field.id}
                      className="flex items-center gap-3 p-3 bg-zinc-50 rounded-xl"
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${
                          field.required ? 'bg-red-400' : 'bg-zinc-300'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-900 truncate">
                          {getLocalizedFieldName(field)}
                          {field.required && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500">{field.type}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* 步骤 2: 字段映射 */}
          {currentStep === 'mapping' && primaryFileAnalysis && (
            <motion.div
              key="mapping"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-4xl mx-auto"
            >
              {/* 工具栏 */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  {savedMappings.length > 0 && (
                    <select
                      value={selectedMappingId}
                      onChange={(e) => handleApplySavedMapping(e.target.value)}
                      className="px-4 py-2 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    >
                      <option value="">{t('templates.apply.selectSavedMapping')}</option>
                      {savedMappings.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <button
                  onClick={() => setShowSaveMappingDialog(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors"
                >
                  <FloppyDiskIcon className="w-4 h-4" />
                  {t('templates.apply.saveMapping')}
                </button>
              </div>

              {/* 映射表格 */}
              <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-zinc-700">
                        {t('templates.apply.templateColumn')}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-zinc-700">
                        {t('templates.apply.type')}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-zinc-700">
                        {t('templates.apply.yourColumn')}
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-zinc-700">
                        {t('templates.apply.matchRate')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                  {fieldsForMapping.map((field) => {
                      const inputKind = field.inputKind || 'excel_column';
                      const secondaryInputNode = inputNodes[1];
                      const analysisForField =
                        inputKind === 'excel_column' &&
                        field.id === 'match_key' &&
                        secondaryInputNode?.id
                          ? (fileAnalyses[secondaryInputNode.id] ?? primaryFileAnalysis)
                          : primaryFileAnalysis;

                      const suggestion = analysisForField.suggestions.find(
                        (s) => s.templateFieldId === field.id
                      );
                      const currentMapping = mappings[field.id];

                      return (
                        <tr key={field.id} className="hover:bg-zinc-50/50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-zinc-900">
                                {getLocalizedFieldName(field)}
                              </span>
                              {field.required && (
                                <span className="text-red-500">*</span>
                              )}
                            </div>
                            {field.description && (
                              <p className="text-xs text-zinc-500 mt-0.5">
                                {field.description}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 text-xs font-medium bg-zinc-100 text-zinc-600 rounded-lg">
                              {field.type}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {inputKind === 'enum' ? (
                              <select
                                value={currentMapping || ''}
                                onChange={(e) => {
                                  setMappings((prev) => ({
                                    ...prev,
                                    [field.id]: e.target.value,
                                  }));
                                }}
                                className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 ${
                                  field.required && !currentMapping
                                    ? 'border-red-200 bg-red-50'
                                    : 'border-zinc-200'
                                }`}
                              >
                                <option value="">
                                  {field.required
                                    ? (t('templates.apply.selectColumn') || '请选择')
                                    : (t('templates.apply.noMapping') || '不选择')}
                                </option>
                                {(field.options || []).map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label || opt.value}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <select
                                value={currentMapping || ''}
                                onChange={(e) => {
                                  setMappings((prev) => ({
                                    ...prev,
                                    [field.id]: e.target.value,
                                  }));
                                }}
                                className={`w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 ${
                                  field.required && !currentMapping
                                    ? 'border-red-200 bg-red-50'
                                    : 'border-zinc-200'
                                }`}
                              >
                                <option value="">
                                  {field.required ? t('templates.apply.selectColumn') : t('templates.apply.noMapping')}
                                </option>
                              {analysisForField.headers.map((header) => (
                                  <option key={header} value={header}>
                                    {header}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {inputKind === 'excel_column' && analysisForField === primaryFileAnalysis && suggestion && suggestion.confidence > 0.3 && (
                              <div className="flex items-center gap-2">
                                <div
                                  className={`h-1.5 w-16 rounded-full ${
                                    suggestion.confidence > 0.8
                                      ? 'bg-emerald-400'
                                      : suggestion.confidence > 0.5
                                      ? 'bg-amber-400'
                                      : 'bg-zinc-300'
                                  }`}
                                  style={{ width: `${suggestion.confidence * 60}px` }}
                                />
                                <span className="text-xs text-zinc-500">
                                  {Math.round(suggestion.confidence * 100)}%
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* 操作按钮 */}
              <div className="flex items-center justify-between mt-6">
                <button
                  onClick={() => setCurrentStep('upload')}
                  className="px-6 py-2 bg-white border border-zinc-200 rounded-xl text-sm font-medium text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                >
                  {t('templates.apply.previous')}
                </button>
                <button
                  onClick={handleExecute}
                  disabled={isExecuting}
                  className="flex items-center gap-2 px-6 py-2 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                >
                  {isExecuting ? (
                    <SpinnerIcon className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" weight="fill" />
                  )}
                  {t('templates.apply.executeTemplate')}
                </button>
              </div>
            </motion.div>
          )}

          {/* 步骤 3: 执行结果 */}
          {currentStep === 'result' && executionResult && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="h-[calc(100vh-200px)]"
            >
              <WorkflowExecutionResult
                result={executionResult}
                showSaveWorkflow={true}
                onSaveWorkflow={handleOpenSaveAsWorkflow}
                onDownloadResult={handleDownloadResult}
                onClose={() => navigate('/templates')}
                title={t('templates.apply.executionResultTitle')}
                subtitle={t('templates.apply.executionResultSubtitle', { name: template.name })}
                showShare={!!executionId}
                onShare={() => setShowShareDialog(true)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 保存映射对话框 */}
      <AnimatePresence>
        {showSaveMappingDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowSaveMappingDialog(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-zinc-900">{t('templates.apply.saveMappingTitle')}</h3>
                <button
                  onClick={() => setShowSaveMappingDialog(false)}
                  className="p-1 text-zinc-400 hover:text-zinc-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-zinc-500 mb-4">
                {t('templates.apply.saveMappingDesc')}
              </p>
              <input
                type="text"
                value={mappingName}
                onChange={(e) => setMappingName(e.target.value)}
                placeholder={t('templates.apply.mappingName')}
                className="w-full px-4 py-2 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 mb-4"
              />
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowSaveMappingDialog(false)}
                  className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
                >
                  {t('templates.apply.cancel')}
                </button>
                <button
                  onClick={handleSaveMapping}
                  disabled={!mappingName.trim()}
                  className="px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                >
                  {t('templates.apply.save')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 保存工作流对话框 */}
      <AnimatePresence>
        {showSaveWorkflowDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowSaveWorkflowDialog(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-zinc-900">
                  {t('templates.apply.saveWorkflowTitle')}
                </h3>
                <button
                  onClick={() => setShowSaveWorkflowDialog(false)}
                  className="p-1 text-zinc-400 hover:text-zinc-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-zinc-500 mb-4">
                {t('templates.apply.saveWorkflowDesc')}
              </p>
              <input
                type="text"
                value={workflowNameDraft}
                onChange={(e) => setWorkflowNameDraft(e.target.value)}
                placeholder={t('templates.apply.workflowNamePlaceholder')}
                className="w-full px-4 py-2 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 mb-4"
              />
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowSaveWorkflowDialog(false)}
                  className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
                >
                  {t('templates.apply.cancel')}
                </button>
                <button
                  onClick={handleSaveAsWorkflow}
                  disabled={!workflowNameDraft.trim()}
                  className="px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                >
                  {t('templates.apply.saveAsWorkflow')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 分享对话框 */}
      <ShareDialog
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        workflowExecutionId={executionId}
        workflowName={template?.name || '模板应用'}
      />
    </div>
  );
}

export default TemplateApplyPage;
